(function () {
  'use strict';

  // Matte PRO is deliberately a separate runtime with its own selectors, state,
  // refresh hook, and debug surface.
  if (window.__LDS_MATTE_COVER_REFRESH__) {
    window.__LDS_MATTE_COVER_REFRESH__();
    return;
  }
  window.__LDS_MATTE_COVER_REFRESH__ = function () {
    if (started) bind(document);
  };

  var CARD_ATTR = 'data-lds-matte-cover-card';
  var CANVAS_ATTR = 'data-lds-matte-cover-canvas';
  var CONFIG_SELECTOR = 'script[type="application/json"][data-lds-matte-cover-config]';
  var READY_CLASS = 'lds-matte-cover-ready';
  var WHITE = '#ffffff';
  var IMAGE_CACHE_LIMIT = 64;
  var states = new Set();
  var stateByCard = new WeakMap();
  var imageCache = new Map();
  var bindTimers = new WeakMap();
  var documentObserver = null;
  var started = false;
  var intersectionObserver = null;
  var activeFlow = null;
  var hoverQuery = null;
  var reducedQuery = null;

  function onMatteCollectionPath() {
    return !!document.body && document.body.classList.contains('hdt-page-type-collection');
  }

  function fineHoverEnabled() {
    return !!window.matchMedia &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isTouchPointer(event) {
    return !!event && (event.pointerType === 'touch' || event.pointerType === 'pen');
  }

  function imageFor(url) {
    if (!url) return Promise.reject(new Error('missing Matte cover asset URL'));
    if (imageCache.has(url)) {
      var cached = imageCache.get(url);
      imageCache.delete(url);
      imageCache.set(url, cached);
      return cached;
    }
    var promise = new Promise(function (resolve, reject) {
      var image = new Image();
      image.decoding = 'async';
      image.crossOrigin = 'anonymous';
      image.onload = function () { resolve(image); };
      image.onerror = function () { reject(new Error('Matte cover asset failed: ' + url)); };
      image.src = url;
    });
    imageCache.set(url, promise);
    promise.catch(function () {
      if (imageCache.get(url) === promise) imageCache.delete(url);
    });
    while (imageCache.size > IMAGE_CACHE_LIMIT) {
      imageCache.delete(imageCache.keys().next().value);
    }
    return promise;
  }

  function containTransform(sourceSize, width, height) {
    var sourceWidth = Number(sourceSize[0]);
    var sourceHeight = Number(sourceSize[1]);
    var scale = Math.min(width / sourceWidth, height / sourceHeight);
    var drawnWidth = sourceWidth * scale;
    var drawnHeight = sourceHeight * scale;
    return {
      scale: scale,
      x: (width - drawnWidth) / 2,
      y: (height - drawnHeight) / 2,
      width: drawnWidth,
      height: drawnHeight
    };
  }

  function clearCanvas(context, width, height) {
    context.globalCompositeOperation = 'source-over';
    context.clearRect(0, 0, width, height);
  }

  function naturalSize(image) {
    return {
      width: Number(image.naturalWidth || image.width || 0),
      height: Number(image.naturalHeight || image.height || 0)
    };
  }

  function positiveNumber(value) {
    return Number.isFinite(Number(value)) && Number(value) > 0;
  }

  function finiteNumber(value) {
    return Number.isFinite(Number(value));
  }

  function validSize(value) {
    return Array.isArray(value) && value.length >= 2 &&
      positiveNumber(value[0]) && positiveNumber(value[1]);
  }

  function validRect(value) {
    return Array.isArray(value) && value.length >= 4 &&
      finiteNumber(value[0]) && finiteNumber(value[1]) &&
      positiveNumber(value[2]) && positiveNumber(value[3]);
  }

  function originalCrop(config) {
    var sourceSize = config.sourceSize;
    var crop = config.sourceCrop || [0, 0, sourceSize[0], sourceSize[1]];
    return [Number(crop[0]), Number(crop[1]), Number(crop[2]), Number(crop[3])];
  }

  function actualCrop(state) {
    var source = naturalSize(state.sourceImage);
    var sourceSize = state.config.sourceSize;
    var crop = originalCrop(state.config);
    var scaleX = source.width / Number(sourceSize[0]);
    var scaleY = source.height / Number(sourceSize[1]);
    return {
      original: crop,
      scaleX: scaleX,
      scaleY: scaleY,
      value: [crop[0] * scaleX, crop[1] * scaleY, crop[2] * scaleX, crop[3] * scaleY]
    };
  }

  function foldMaskLuminanceIntoAlpha(state) {
    var imageData;
    try {
      imageData = state.maskContext.getImageData(0, 0, state.pixelWidth, state.pixelHeight);
    } catch (error) {
      throw new Error('same-origin Matte mask readback unavailable: ' + error.message);
    }
    var pixels = imageData.data;
    for (var i = 0; i < pixels.length; i += 4) {
      var shade = Math.max(pixels[i], pixels[i + 1], pixels[i + 2]);
      pixels[i + 3] = Math.round(pixels[i + 3] * shade / 255);
      pixels[i] = 255;
      pixels[i + 1] = 255;
      pixels[i + 2] = 255;
    }
    state.maskContext.putImageData(imageData, 0, 0);
  }

  // The tint canvas contains only mask pixels. Its transparent area can never
  // replace the source image, price, actions, or any other card content.
  function makeMaskLayer(state, tint) {
    var width = state.pixelWidth;
    var height = state.pixelHeight;
    var context = state.tintContext;
    clearCanvas(context, width, height);
    context.drawImage(state.maskCanvas, 0, 0, width, height);
    context.globalCompositeOperation = 'source-in';
    if (typeof tint === 'string') {
      context.fillStyle = tint;
    } else {
      var phase = (Number(tint || 0) * 0.00008) % 1;
      var shift = (phase * width * 2) % (width * 2);
      var gradient = context.createLinearGradient(-width + shift, 0, width + shift, height * 0.08);
      var hue = phase * 360;
      gradient.addColorStop(0, 'hsl(' + ((hue + 0) % 360).toFixed(1) + ' 100% 60%)');
      gradient.addColorStop(0.25, 'hsl(' + ((hue + 72) % 360).toFixed(1) + ' 100% 60%)');
      gradient.addColorStop(0.5, 'hsl(' + ((hue + 144) % 360).toFixed(1) + ' 100% 60%)');
      gradient.addColorStop(0.75, 'hsl(' + ((hue + 216) % 360).toFixed(1) + ' 100% 60%)');
      gradient.addColorStop(1, 'hsl(' + ((hue + 288) % 360).toFixed(1) + ' 100% 60%)');
      context.fillStyle = gradient;
    }
    context.fillRect(0, 0, width, height);
    context.globalCompositeOperation = 'source-over';
  }

  function drawStaticWhite(state) {
    if (!state.ready || !state.pixelWidth || !state.pixelHeight) return;
    clearCanvas(state.context, state.pixelWidth, state.pixelHeight);
    state.context.drawImage(state.baseCanvas, 0, 0, state.pixelWidth, state.pixelHeight);
    state.baseline = 'staticWhite';
  }

  function drawFlow(state, timestamp) {
    if (!state.ready || !state.maskImage || !state.nativeFlow) return;
    drawStaticWhite(state);
    makeMaskLayer(state, Number(timestamp || 0));
    state.context.drawImage(state.tintCanvas, 0, 0, state.pixelWidth, state.pixelHeight);
  }

  function prepareCanvases(state) {
    if (state.removed || !state.card.isConnected || !state.sourceImage ||
        (state.requiresMask && !state.maskImage)) return false;
    var rect = state.link.getBoundingClientRect();
    var width = Math.round(rect.width);
    var height = Math.round(rect.height);
    if (!width || !height) return false;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var pixelWidth = Math.max(1, Math.round(width * dpr));
    var pixelHeight = Math.max(1, Math.round(height * dpr));
    state.width = width;
    state.height = height;
    state.dpr = dpr;
    state.pixelWidth = pixelWidth;
    state.pixelHeight = pixelHeight;
    state.canvas.width = pixelWidth;
    state.canvas.height = pixelHeight;
    state.baseCanvas.width = pixelWidth;
    state.baseCanvas.height = pixelHeight;
    state.maskCanvas.width = pixelWidth;
    state.maskCanvas.height = pixelHeight;
    state.tintCanvas.width = pixelWidth;
    state.tintCanvas.height = pixelHeight;

    // The source is served at a CDN width that may differ from sourceSize. Keep
    // all configured crop and bbox coordinates in their original image space.
    var crop = actualCrop(state);
    state.crop = crop;
    state.transform = containTransform([crop.value[2], crop.value[3]], width, height);

    var sourceContext = state.baseContext;
    clearCanvas(sourceContext, pixelWidth, pixelHeight);
    sourceContext.fillStyle = WHITE;
    sourceContext.fillRect(0, 0, pixelWidth, pixelHeight);
    var transform = state.transform;
    sourceContext.drawImage(
      state.sourceImage,
      crop.value[0], crop.value[1], crop.value[2], crop.value[3],
      transform.x * dpr,
      transform.y * dpr,
      transform.width * dpr,
      transform.height * dpr
    );

    var maskContext = state.maskContext;
    clearCanvas(maskContext, pixelWidth, pixelHeight);
    if (state.maskImage) {
      var bbox = state.config.bbox || [0, 0, state.config.maskSize[0], state.config.maskSize[1]];
      var maskNatural = naturalSize(state.maskImage);
      var maskSize = [Number(state.config.maskSize[0]), Number(state.config.maskSize[1])];
      var maskScaleX = maskNatural.width / maskSize[0];
      var maskScaleY = maskNatural.height / maskSize[1];
      var sourceX = Number(bbox[0]) * crop.scaleX;
      var sourceY = Number(bbox[1]) * crop.scaleY;
      var sourceWidth = maskSize[0] * crop.scaleX;
      var sourceHeight = maskSize[1] * crop.scaleY;
      maskContext.drawImage(
        state.maskImage,
        (transform.x + (sourceX - crop.value[0]) * transform.scale) * dpr,
        (transform.y + (sourceY - crop.value[1]) * transform.scale) * dpr,
        (sourceWidth * transform.scale) * dpr,
        (sourceHeight * transform.scale) * dpr
      );
      state.maskNaturalScale = [maskScaleX, maskScaleY];
      foldMaskLuminanceIntoAlpha(state);
    }

    // staticWhite is the baseline for every device. The approved mask adds a
    // neutral, source-derived white overlay before any optional FLOW frames.
    if (state.config.whiteMaskOverlay && state.maskImage) {
      makeMaskLayer(state, WHITE);
      sourceContext.drawImage(state.tintCanvas, 0, 0, pixelWidth, pixelHeight);
    }
    state.ready = true;
    drawStaticWhite(state);
    return true;
  }

  function stopFlow(state) {
    state.active = false;
    if (activeFlow === state) activeFlow = null;
    if (state.raf) {
      window.cancelAnimationFrame(state.raf);
      state.raf = 0;
    }
    if (state.ready) drawStaticWhite(state);
  }

  function frame(state, timestamp) {
    state.raf = 0;
    if (!state.active || activeFlow !== state || !state.ready || !state.hovered ||
        !state.nativeFlow || !fineHoverEnabled() || !state.card.isConnected ||
        document.visibilityState === 'hidden') {
      stopFlow(state);
      return;
    }
    drawFlow(state, timestamp);
    state.frames += 1;
    state.raf = window.requestAnimationFrame(function (nextTimestamp) {
      frame(state, nextTimestamp);
    });
  }

  function startFlow(state) {
    if (state.removed || !state.nativeFlow || !state.ready || !state.maskImage ||
        !fineHoverEnabled() || !state.hovered || document.visibilityState === 'hidden') return;
    if (activeFlow && activeFlow !== state) {
      activeFlow.hovered = false;
      stopFlow(activeFlow);
    }
    activeFlow = state;
    state.active = true;
    if (!state.raf) state.raf = window.requestAnimationFrame(function (timestamp) {
      frame(state, timestamp);
    });
  }

  function loadState(state) {
    if (state.removed) return Promise.resolve(state);
    if (state.loadPromise) return state.loadPromise;
    state.sourceRequested = true;
    state.maskRequested = !!state.config.maskUrl;
    var maskPromise = state.config.maskUrl ? imageFor(state.config.maskUrl) : Promise.resolve(null);
    state.loadPromise = Promise.all([imageFor(state.config.sourceUrl), maskPromise]).then(function (images) {
      if (state.removed || !state.card.isConnected) return state;
      state.sourceImage = images[0];
      state.maskImage = images[1];
      if (state.removed || !state.card.isConnected) return state;
      if (!prepareCanvases(state)) return state;
      if (state.removed || !state.card.isConnected) return state;
      state.card.classList.add(READY_CLASS);
      if (state.hovered) startFlow(state);
      return state;
    }).catch(function (error) {
      failState(state, error);
      throw error;
    });
    return state.loadPromise;
  }

  function onPointerEnter(state, event) {
    // Touch and pen keep the native anchor's normal one-tap navigation. A
    // near-viewport intersection may still install the staticWhite canvas.
    if (isTouchPointer(event) || !fineHoverEnabled()) {
      state.hovered = false;
      stopFlow(state);
      return;
    }
    state.hovered = true;
    state.visible = true;
    loadState(state).then(function () {
      if (state.hovered) startFlow(state);
    }).catch(function () {});
  }

  function onPointerLeave(state) {
    state.hovered = false;
    stopFlow(state);
  }

  function removeState(state) {
    if (state.removed) return;
    state.removed = true;
    stopFlow(state);
    state.hovered = false;
    state.card.removeEventListener('pointerenter', state.onEnter);
    state.card.removeEventListener('pointerleave', state.onLeave);
    state.card.classList.remove(READY_CLASS);
    state.card.removeAttribute(CARD_ATTR);
    if (state.resizeObserver) state.resizeObserver.disconnect();
    if (intersectionObserver) intersectionObserver.unobserve(state.media);
    if (state.canvas.parentNode) state.canvas.parentNode.removeChild(state.canvas);
    state.canvas.width = 0;
    state.canvas.height = 0;
    state.baseCanvas.width = 0;
    state.baseCanvas.height = 0;
    state.maskCanvas.width = 0;
    state.maskCanvas.height = 0;
    state.tintCanvas.width = 0;
    state.tintCanvas.height = 0;
    state.sourceImage = null;
    state.maskImage = null;
    state.crop = null;
    state.transform = null;
    stateByCard.delete(state.card);
    states.delete(state);
  }

  function failState(state, error) {
    removeState(state);
    if (window.console && console.warn) console.warn('[lds-matte-cover] preserving native card media', error);
  }

  function makeState(wrapper, card, media, link, config, configNode) {
    var canvas = link.querySelector('[' + CANVAS_ATTR + ']');
    if (!canvas) canvas = document.createElement('canvas');
    canvas.className = 'lds-matte-cover-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    canvas.setAttribute('role', 'presentation');
    canvas.setAttribute(CANVAS_ATTR, '');
    canvas.width = 0;
    canvas.height = 0;
    if (!canvas.parentNode) link.appendChild(canvas);
    card.classList.remove(READY_CLASS);
    // Existing card configs call this switch `flow`; an explicit nativeFlow
    // value takes precedence so a future config can disable it safely.
    var nativeFlow = config.nativeFlow === undefined ? config.flow === true : config.nativeFlow === true;
    var requiresMask = !!config.maskUrl && (config.whiteMaskOverlay !== false || nativeFlow);
    var state = {
      wrapper: wrapper,
      card: card,
      media: media,
      link: link,
      config: config,
      configNode: configNode,
      configText: configNode.textContent || '',
      nativeFlow: nativeFlow,
      baseline: 'staticWhite',
      requiresMask: requiresMask,
      canvas: canvas,
      context: canvas.getContext('2d'),
      baseCanvas: document.createElement('canvas'),
      maskCanvas: document.createElement('canvas'),
      tintCanvas: document.createElement('canvas'),
      sourceImage: null,
      maskImage: null,
      baseContext: null,
      maskContext: null,
      tintContext: null,
      loadPromise: null,
      sourceRequested: false,
      maskRequested: false,
      removed: false,
      ready: false,
      active: false,
      hovered: false,
      visible: false,
      raf: 0,
      frames: 0,
      width: 0,
      height: 0,
      pixelWidth: 0,
      pixelHeight: 0,
      transform: null,
      crop: null,
      resizeObserver: null,
      onEnter: null,
      onLeave: null
    };
    state.baseContext = state.baseCanvas.getContext('2d');
    state.maskContext = state.maskCanvas.getContext('2d');
    state.tintContext = state.tintCanvas.getContext('2d');
    state.onEnter = function (event) { onPointerEnter(state, event); };
    state.onLeave = function () { onPointerLeave(state); };
    card.setAttribute(CARD_ATTR, '');
    card.addEventListener('pointerenter', state.onEnter);
    card.addEventListener('pointerleave', state.onLeave);
    if (typeof ResizeObserver === 'function') {
      state.resizeObserver = new ResizeObserver(function () {
        if (!state.sourceImage || (state.requiresMask && !state.maskImage)) return;
        try {
          if (prepareCanvases(state)) {
            card.classList.add(READY_CLASS);
            if (state.hovered) startFlow(state);
          }
        } catch (error) {
          failState(state, error);
        }
      });
      state.resizeObserver.observe(link);
    }
    if (intersectionObserver) intersectionObserver.observe(media);
    states.add(state);
    stateByCard.set(card, state);
    return state;
  }

  function ownerForConfig(node) {
    if (!node || !node.closest) return null;
    return node.closest('hdt-card-product[data-lds-product-id]') || node.closest('[data-product-id]');
  }

  function configuredWrappers(root) {
    var nodes = Array.prototype.slice.call(root.querySelectorAll(CONFIG_SELECTOR));
    var wrappers = [];
    nodes.forEach(function (node) {
      var wrapper = ownerForConfig(node);
      if (wrapper && wrappers.indexOf(wrapper) === -1) wrappers.push(wrapper);
    });
    return wrappers;
  }

  function cardFor(wrapper) {
    var card = wrapper.matches && wrapper.matches('hdt-card-product') ? wrapper : wrapper.querySelector('hdt-card-product');
    var scope = card || wrapper;
    var media = scope.querySelector('.hdt-card-product__media');
    var link = scope.querySelector('.hdt-card-product__media-wrapp');
    if (!card || !media || !link) return null;
    return { card: card, media: media, link: link };
  }

  function configFor(wrapper) {
    var node = wrapper.querySelector(CONFIG_SELECTOR);
    if (!node) return null;
    try {
      var config = JSON.parse(node.textContent || '{}');
      var wrapperId = String(wrapper.getAttribute('data-lds-product-id') || wrapper.getAttribute('data-product-id') || '');
      if (!config || !wrapperId || config.id === undefined || config.id === null ||
          String(config.id) !== wrapperId || typeof config.sourceUrl !== 'string' ||
          !config.sourceUrl || !validSize(config.sourceSize) ||
          !positiveNumber(config.sourceSize[0]) || !positiveNumber(config.sourceSize[1])) return null;
      if (config.sourceCrop && !validRect(config.sourceCrop)) return null;
      if (config.maskUrl && (typeof config.maskUrl !== 'string' ||
          !validSize(config.maskSize) || !validRect(config.bbox))) return null;
      config.id = String(config.id);
      return { config: config, node: node };
    } catch (error) {
      if (window.console && console.warn) console.warn('[lds-matte-cover] invalid per-card config', error);
      return null;
    }
  }

  function clearStates() {
    Array.from(states).forEach(removeState);
  }

  function bind(root) {
    if (!onMatteCollectionPath()) {
      clearStates();
      return;
    }
    if (!root || !root.querySelectorAll) return;
    states.forEach(function (state) {
      if (!state.card.isConnected || !state.configNode.isConnected || !state.wrapper.contains(state.configNode) ||
          state.configText !== (state.configNode.textContent || '')) removeState(state);
    });
    configuredWrappers(root).forEach(function (wrapper) {
      var parsed = configFor(wrapper);
      if (!parsed) return;
      var parts = cardFor(wrapper);
      if (!parts) return;
      var existing = stateByCard.get(parts.card);
      if (existing) {
        if (existing.configNode !== parsed.node || existing.configText !== (parsed.node.textContent || '')) {
          removeState(existing);
        } else {
          return;
        }
      }
      makeState(wrapper, parts.card, parts.media, parts.link, parsed.config, parsed.node);
    });
  }

  function queueBind(root) {
    if (!root || !root.querySelector) return;
    if (bindTimers.has(root)) return;
    var timer = window.setTimeout(function () {
      bindTimers.delete(root);
      bind(root);
    }, 0);
    bindTimers.set(root, timer);
  }

  function runtimeOnlyNode(node) {
    return node && node.nodeType === 1 && (
      node.matches('[' + CANVAS_ATTR + ']') ||
      node.closest('[' + CARD_ATTR + ']')
    );
  }

  function relevantMutationNode(node) {
    if (!node) return false;
    if (node.nodeType === 3) return !!node.parentElement && !!node.parentElement.closest(CONFIG_SELECTOR);
    if (node.nodeType !== 1) return false;
    if (runtimeOnlyNode(node)) return node.matches('[data-lds-product-id]') || node.matches('[data-product-id]');
    return node.matches('[data-lds-product-id]') || node.matches('[data-product-id]') || node.matches(CONFIG_SELECTOR) ||
      !!node.querySelector(CONFIG_SELECTOR) || !!node.querySelector('hdt-card-product[data-lds-product-id]');
  }

  function observeDocument() {
    if (documentObserver || typeof MutationObserver !== 'function' || !document.documentElement) return;
    documentObserver = new MutationObserver(function (records) {
      var changed = records.some(function (record) {
        if (record.type === 'characterData') return !!record.target.parentElement &&
          !!record.target.parentElement.closest(CONFIG_SELECTOR);
        if (record.type === 'attributes') return record.target.matches('[data-lds-product-id]') || record.target.matches('[data-product-id]');
        var nodes = Array.prototype.slice.call(record.addedNodes).concat(Array.prototype.slice.call(record.removedNodes));
        return nodes.some(relevantMutationNode);
      });
      if (changed) queueBind(document);
    });
    documentObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['data-lds-product-id', 'data-product-id']
    });
  }

  function bindAfterPilotUpdate(event) {
    var target = event && event.target;
    if (!target || !target.querySelector) return;
    window.queueMicrotask ? window.queueMicrotask(function () { bind(target); }) : queueBind(target);
  }

  function capabilityChanged() {
    if (!fineHoverEnabled()) {
      Array.from(states).forEach(stopFlow);
      return;
    }
    states.forEach(function (state) {
      if (state.hovered) {
        loadState(state).then(function () { if (state.hovered) startFlow(state); }).catch(function () {});
      }
    });
  }

  function watchMediaQuery(query) {
    if (!query) return;
    if (query.addEventListener) query.addEventListener('change', capabilityChanged);
    else if (query.addListener) query.addListener(capabilityChanged);
  }

  window.__LDS_MATTE_COVERS__ = function () {
    return Array.from(states).map(function (state) {
      return {
        id: String(state.config.id),
        ready: !!state.ready,
        baseline: state.baseline,
        nativeFlow: !!state.nativeFlow,
        active: !!state.active,
        raf: !!state.raf,
        frames: Number(state.frames || 0),
        visible: !!state.visible,
        hovered: !!state.hovered,
        sourceRequested: !!state.sourceRequested,
        maskRequested: !!state.maskRequested
      };
    });
  };

  function start() {
    if (started) return;
    started = true;
    hoverQuery = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)') : null;
    reducedQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    watchMediaQuery(hoverQuery);
    watchMediaQuery(reducedQuery);
    intersectionObserver = typeof IntersectionObserver === 'function' ? new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var state = Array.from(states).find(function (candidate) { return candidate.media === entry.target; });
        if (!state) return;
        state.visible = !!entry.isIntersecting;
        if (state.visible) {
          loadState(state).catch(function () {});
        } else {
          stopFlow(state);
        }
      });
    }, { rootMargin: '400px 0px' }) : null;
    observeDocument();
    document.addEventListener('pilot:updated', bindAfterPilotUpdate);
    window.addEventListener('popstate', function () { bind(document); });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') Array.from(states).forEach(stopFlow);
      else capabilityChanged();
    });
    bind(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
}());
